<script lang="ts" setup>
const props = defineProps<{
  status: string; // Used to handle colours/icons, e.g. warning, error, info, success
  title: string; // Main title
  message?: string; // Displayed below the title,
  closable?: { type: boolean; default: false };
}>();

let colours = {
  bg: 'bg-yellow-50',
  border: 'border-yellow-100',
  iconText: 'text-yellow-400',
  titleText: 'text-yellow-800',
  messageText: 'text-yellow-700',
  closeBg: 'bg-yellow-50',
  closeText: 'text-yellow-500',
};
if (props.status == 'success')
  colours = {
    bg: 'bg-green-50',
    border: 'border-green-100',
    iconText: 'text-green-500',
    titleText: 'text-green-800',
    messageText: 'text-green-700',
    closeBg: 'bg-green-50',
    closeText: 'text-green-700',
  };
if (props.status == 'warning')
  colours = {
    bg: 'bg-yellow-50',
    border: 'border-yellow-100',
    iconText: 'text-yellow-400',
    titleText: 'text-yellow-800',
    messageText: 'text-yellow-700',
    closeBg: 'bg-yellow-50',
    closeText: 'text-yellow-500',
  };
if (props.status == 'error')
  colours = {
    bg: 'bg-red-50',
    border: 'border-red-100',
    iconText: 'text-red-400',
    titleText: 'text-red-800',
    messageText: 'text-red-700',
    closeBg: 'bg-red-50',
    closeText: 'text-red-500',
  };
if (props.status == 'info')
  colours = {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconText: 'text-blue-500',
    titleText: 'text-blue-800',
    messageText: 'text-blue-700',
    closeBg: 'bg-blue-50',
    closeText: 'text-blue-500',
  };
if (props.status == 'neutral')
  colours = {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    iconText: 'text-gray-500',
    titleText: 'text-gray-800',
    messageText: 'text-gray-700',
    closeBg: 'bg-gray-50',
    closeText: 'text-gray-500',
  };

defineEmits(['close']);
</script>

<template>
  <div class="rounded-md border p-4 shadow mb-4" :class="[colours.bg, colours.border]">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg
          v-if="props.status == 'success'"
          class="h-5 w-5"
          :class="colours.iconText"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-if="props.status == 'info'"
          class="h-5 w-5"
          :class="colours.iconText"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M19 10.5a8.5 8.5 0 11-17 0 8.5 8.5 0 0117 0zM8.25 9.75A.75.75 0 019 9h.253a1.75 1.75 0 011.709 2.13l-.46 2.066a.25.25 0 00.245.304H11a.75.75 0 010 1.5h-.253a1.75 1.75 0 01-1.709-2.13l.46-2.066a.25.25 0 00-.245-.304H9a.75.75 0 01-.75-.75zM10 7a1 1 0 100-2 1 1 0 000 2z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-if="props.status == 'warning'"
          class="h-5 w-5"
          :class="colours.iconText"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-if="props.status == 'error'"
          class="h-5 w-5"
          :class="colours.iconText"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-bold" :class="colours.titleText">{{ props.title }}</h3>
        <div v-if="props.message" class="mt-2 text-sm" :class="colours.messageText">
          <p v-html="props.message"></p>
        </div>
        <slot></slot>
      </div>
      <div class="ml-auto pl-3">
        <div class="-mr-3 -my-3" v-if="closable">
          <button
            type="button"
            class="inline-flex rounded-md"
            :class="[colours.closeBg, colours.closeText]"
            @click="$emit('close')"
          >
            <span class="sr-only">Dismiss</span>
            <svg
              class="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
